/**
 * useDraft.js
 * -----------
 * Reusable hook for all four service forms.
 *
 * Provides:
 *   - draftLoading   — true while fetching draft from server
 *   - existingDraft  — the raw draft object from the server (or null)
 *   - draftId        — current draft id being worked on
 *   - draftDecision  — 'pending' | 'continue' | 'new' | 'none'
 *   - chooseContinue — call to restore saved draft
 *   - chooseNew      — call to discard saved draft and start fresh
 *   - saveProgress   — debounced auto-save (call on every form change)
 *   - submitForm     — submit the form (clears draft on success)
 *
 * Usage:
 *   const draft = useDraft('private_aviation', formState, setFormState, emptyState)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { getDraft, saveDraft, deleteDraft, submitRequest } from './api'

const AUTOSAVE_DELAY = 1500   // ms — debounce window

/**
 * @param {string}   service      — service slug
 * @param {object}   formState    — current form state
 * @param {function} setFormState — state setter
 * @param {object}   emptyState   — blank form defaults (used when starting fresh)
 */
export function useDraft(service, formState, setFormState, emptyState) {
  const [draftLoading,  setDraftLoading]  = useState(true)
  const [existingDraft, setExistingDraft] = useState(null)
  const [draftId,       setDraftId]       = useState(null)
  const [draftDecision, setDraftDecision] = useState('pending') // pending|continue|new|none

  const autosaveTimer = useRef(null)
  const lastSaved     = useRef(null)

  /* ── Load draft on mount ── */
  useEffect(() => {
    setDraftLoading(true)
    getDraft(service)
      .then(({ draft }) => {
        if (draft && draft.payload) {
          setExistingDraft(draft)
          setDraftDecision('pending')
        } else {
          setDraftDecision('none')
        }
      })
      .catch(() => {
        setDraftDecision('none')
      })
      .finally(() => setDraftLoading(false))
  }, [service])

  /* ── User chooses to continue their saved draft ── */
  const chooseContinue = useCallback(() => {
    if (!existingDraft) return
    setFormState(prev => ({ ...prev, ...existingDraft.payload }))
    setDraftId(existingDraft.id)
    setDraftDecision('continue')
  }, [existingDraft, setFormState])

  /* ── User chooses to discard draft and start fresh ── */
  const chooseNew = useCallback(async () => {
    if (existingDraft) {
      try {
        await deleteDraft(service, existingDraft.id)
      } catch (_) {
        // Non-fatal — continue regardless
      }
    }
    setFormState(emptyState)
    setDraftId(null)
    setExistingDraft(null)
    setDraftDecision('new')
  }, [existingDraft, service, setFormState, emptyState])

  /* ── Auto-save: debounced, fires when formState changes ── */
  const saveProgress = useCallback((currentFormState) => {
    if (draftDecision === 'pending') return  // don't save until user decides

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)

    autosaveTimer.current = setTimeout(async () => {
      try {
        const { draft_id } = await saveDraft(service, currentFormState, draftId)
        if (!draftId) setDraftId(draft_id)
        lastSaved.current = new Date()
      } catch (_) {
        // Silently fail — don't disrupt the user
      }
    }, AUTOSAVE_DELAY)
  }, [service, draftId, draftDecision])

  /* ── Submit ── */
  const submitForm = useCallback(async (finalPayload) => {
    const result = await submitRequest(service, finalPayload, draftId)
    // Draft is consumed — clear local state
    setDraftId(null)
    setExistingDraft(null)
    return result
  }, [service, draftId])

  /* ── Cleanup ── */
  useEffect(() => {
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    }
  }, [])

  return {
    draftLoading,
    existingDraft,
    draftId,
    draftDecision,
    chooseContinue,
    chooseNew,
    saveProgress,
    submitForm,
  }
}